import { Box, Button, Grid, GridItem } from "@chakra-ui/react";
import {
    withDatasourceCheck,
    type ComponentParams,
    type ComponentRendering,
    type GetStaticComponentProps,
} from "@sitecore-jss/sitecore-jss-nextjs";

import GenericHeader, { type GenericHeaderProps } from "commons/ui/GenericHeader";
import { useCallback, useEffect, useState } from "react";
import { ModalNextArrow, ModalOverlay, ModalPrevArrow } from "commons/ModalOverlay";
import graphqlClientFactory from "lib/graphql-client-factory";
import useImageSizes from "utils/hooks/useImageSizes";
import { ChakraNextImage } from "commons/sc/SCImage";
import { pulseBgLoadingAnimation, pulseLoadingAnimation } from "themes/foundations/animations";

interface ImageGallery {
    item: { imagePath: { value: string } };
}

interface ImageResult {
    id: string;
    url: {
        url: string;
    };
    altText: {
        value: string;
    } | null;
    width: {
        value: string;
    };
    height: {
        value: string;
    };
}

interface PageInfo {
    endCursor: string;
    hasNext: boolean;
}

interface ImagePath {
    pageInfo: PageInfo;
    results: ImageResult[];
    total?: number;
}

export type ImageGalleryProps = {
    rendering: ComponentRendering & { params: ComponentParams };
    params: { [key: string]: string };
    fields: GenericHeaderProps;
    ImagePath: ImagePath;
};

const ImageGallery = (props: ImageGalleryProps) => {
    const sizes = useImageSizes({ base: 1, sm: 1 / 2, md: 1 / 3 }, true);
    const overlaySizes = useImageSizes({ base: 1, sm: 1, md: 1 }, true);

    const [openImgIndex, setOpenImgIndex] = useState<number>();

    const [modalImageLoading, setModalImageLoading] = useState<boolean>(true);
    const images = props.ImagePath.results;
    const modalOpen = openImgIndex != undefined;

    const goToNext = useCallback(() => {
        let newIndex = (openImgIndex ?? 0) + 1;
        if (newIndex >= images.length) newIndex = 0;

        setModalImageLoading(true);
        setOpenImgIndex(newIndex);
    }, [images, openImgIndex]);

    const goToPrev = useCallback(() => {
        let newIndex = (openImgIndex ?? 0) - 1;
        if (newIndex < 0) newIndex = images.length - 1;

        setModalImageLoading(true);
        setOpenImgIndex(newIndex);
    }, [images, openImgIndex]);

    const openOverlay = (index: number) => {
        setModalImageLoading(true);
        setOpenImgIndex(index);
    };

    const closeOverlay = () => {
        setOpenImgIndex(undefined);
    };

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!modalOpen) return;

            if (event.key === "ArrowLeft") {
                goToPrev();
            } else if (event.key === "ArrowRight") {
                goToNext();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [goToNext, goToPrev, modalOpen]);

    return (
        <GenericHeader
            Id={props.rendering?.uid ?? props.rendering?.componentName}
            AnchorId={props.fields?.AnchorId}
            HeaderHeadline={props.fields?.HeaderHeadline}
            HeaderSubHeadline={props.fields?.HeaderSubHeadline}
            HeaderCTA={props.fields?.HeaderCTA}
            headlineSize={props.params?.HeaderHeadlineSize}
            spacingSize={props.params?.Padding}
        >
            <Grid
                templateColumns={{
                    base: "repeat(1, 1fr)",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                }}
                columnGap="sp12"
                rowGap="sp36"
            >
                {images.map((image, index) => (
                    <GridItem
                        as={"button"}
                        key={image.id}
                        onClick={() => openOverlay(index)}
                        cursor="pointer"
                    >
                        <Box position={"relative"}>
                            <ChakraNextImage
                                src={image.url.url}
                                alt={image.altText?.value ?? ""}
                                width={parseInt(image.width?.value) ?? 0}
                                height={parseInt(image.height?.value) ?? 0}
                                sizes={sizes}
                                objectFit={"contain"}
                                loading="lazy"
                                sx={{ w: "100%", h: "auto" }}
                            />
                            <Box
                                animation={`${pulseLoadingAnimation} infinite 2s linear`}
                                bgColor={"brand.lightGray"}
                                position={"absolute"}
                                top={0}
                                left={0}
                                width="full"
                                height="full"
                                zIndex={-1}
                            ></Box>
                        </Box>
                    </GridItem>
                ))}
            </Grid>

            {modalOpen && (
                <ModalOverlay
                    closeOverlay={closeOverlay}
                    next={
                        <Button
                            variant="unstyled"
                            w={"sp40"}
                            h={"sp40"}
                            onClick={goToNext}
                            isLoading={modalImageLoading}
                            leftIcon={<ModalNextArrow />}
                        ></Button>
                    }
                    prev={
                        <Button
                            variant="unstyled"
                            w={"sp40"}
                            h={"sp40"}
                            onClick={goToPrev}
                            isLoading={modalImageLoading}
                            leftIcon={<ModalPrevArrow />}
                        ></Button>
                    }
                >
                    <Box
                        animation={`${modalImageLoading && pulseBgLoadingAnimation} infinite 2s linear`}
                    >
                        <ChakraNextImage
                            src={images[openImgIndex].url.url}
                            alt={images[openImgIndex].altText?.value ?? ""}
                            width={parseInt(images[openImgIndex].width?.value) ?? 0}
                            height={parseInt(images[openImgIndex].height?.value) ?? 0}
                            sx={{
                                maxH: [null, null, "calc(80vh - 160px)"],
                                opacity: modalImageLoading ? 0 : 1,
                                zIndex: 1,
                                position: "relative",
                                objectFit: "contain",
                            }}
                            onLoad={() => setModalImageLoading(false)}
                            quality={95}
                            sizes={overlaySizes}
                        />
                    </Box>
                </ModalOverlay>
            )}
        </GenericHeader>
    );
};

export const getStaticProps: GetStaticComponentProps = async (rendering, layoutData) => {
    const graphQLClient = graphqlClientFactory();

    // Retrieve ImageGallery data. Only need the ImagePath to retrieve the media folder
    const queryDataSource = `
    query search($datasource: String!, $language: String!) {
        item(path: $datasource, language: $language) {
            ...on ImageGallery {
                imagePath: field(name: "ImagePath") {
                    value
                }
            }
        }
    }`;

    const imageGallery = await graphQLClient.request<ImageGallery>(queryDataSource, {
        datasource: rendering.dataSource,
        contextItem: layoutData?.sitecore?.route?.itemId,
        language: layoutData?.sitecore?.context?.language,
    });

    // ImagePath not defined
    if (!imageGallery?.item?.imagePath?.value)
        return {
            ImagePath: { results: [] },
        };

    const fetchImages = async (after: string) => {
        const queryImages = `
        query {
            ImagePath: search(
                where: {
                AND: [
                    {
                    name: "_path"
                    value: "${imageGallery.item.imagePath.value}"
                    operator: CONTAINS
                    }
                ]
                }
                first: 40
                after: "${after}"
            ) {
                total
                pageInfo {
                    endCursor
                    hasNext
                }
                results {
                    id
                    url {
                        url
                    }
                    altText: field(name: "Alt") {
                        value
                    }
                    width: field(name: "Width") {
                        value
                    }
                    height: field(name: "Height") {
                        value
                    }
                }
            }
        }`;

        return await graphQLClient.request<ImageGalleryProps>(queryImages);
    };

    let response = await fetchImages("");
    let allResults = [...response.ImagePath.results];

    while (response.ImagePath.pageInfo.hasNext) {
        response = await fetchImages(response.ImagePath.pageInfo.endCursor);
        allResults = [...allResults, ...response.ImagePath.results];
    }

    const responseData = {
        ImagePath: {
            ...response.ImagePath,
            results: allResults,
            total: allResults.length,
        },
    };

    const filteredResults =
        responseData?.ImagePath?.results?.filter((img: ImageResult) =>
            img.url.url.includes("/media/")
        ) ?? [];

    return {
        ImagePath: {
            ...responseData.ImagePath,
            results: filteredResults,
            total: filteredResults.length,
        },
    };
};

export default withDatasourceCheck()<ImageGalleryProps>(ImageGallery);
